import { Controller } from "@hotwired/stimulus"

// Keeps the YouTube analytics store page honest while a collection is in flight.
//
// WHY THIS EXISTS. A collector pass is dominated by the per-video settling re-pull —
// roughly two reports.query calls per live video — so a channel takes about ninety
// seconds and three channels used to take just under three minutes in series. Nothing
// on the page moved during that time, and the four freshness stamps only change when
// Google actually had something new to give, so a run in progress, a run that finished
// quietly, and a run that died on its first API call all looked identical. That is how
// a sweep that visited every channel got read as "it only did the first one".
//
// Two independent clocks, deliberately:
//   * a POLL against /admin/youtube_analytics/status (run table only, no aggregates)
//     that moves badges between states, and
//   * a one-second TICK that advances the elapsed counter locally.
// The tick matters on its own — between polls the card would otherwise sit frozen,
// which is the exact ambiguity this is here to remove.
//
// When the last active run finishes the page reloads ONCE, because the numbers this
// controller does not touch — video counts, freshness stamps, fact totals — are the
// whole reason the operator pressed the button.
export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 4000 },
    // ~30 minutes at the default interval, the same horizon after which the server
    // calls an unfinished run abandoned. A tab left open overnight stops polling.
    maxAttempts: { type: Number, default: 450 },
  }

  // Mirrors Yt::CollectorRun::RUN_BADGES. Duplicated rather than shipped in the
  // payload so a poll response stays small; the server sends `state` and this maps it,
  // exactly as the ERB does.
  static LABELS = {
    queued: "Queued…",
    running: "Collecting…",
    slow: "Still collecting",
    abandoned: "Unknown — no result",
    failed: "Last run failed",
    succeeded: "Last run OK",
    never: "Never run",
  }

  static CLASSES = {
    queued: "bg-blue-100 text-blue-800",
    running: "bg-blue-100 text-blue-800",
    slow: "bg-amber-100 text-amber-800",
    abandoned: "bg-red-100 text-red-800",
    failed: "bg-red-100 text-red-800",
    succeeded: "bg-emerald-100 text-emerald-800",
    never: "bg-gray-100 text-gray-600",
  }

  static ACTIVE = ["queued", "running", "slow"]

  connect() {
    this.attempts = 0
    this.sawActive = this.anyActiveInDom()
    this.startTicking()
    if (this.sawActive) this.startPolling()
  }

  disconnect() {
    this.stopPolling()
    this.stopTicking()
  }

  // Public so the "Run collector now" button can start the watch immediately rather
  // than waiting for the redirect's fresh render to happen to contain an active card.
  watch() {
    this.sawActive = true
    this.startPolling()
  }

  startPolling() {
    if (this.pollTimer) return
    this.pollTimer = setInterval(() => this.poll(), this.intervalValue)
  }

  stopPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
  }

  startTicking() {
    if (this.tickTimer) return
    this.tickTimer = setInterval(() => this.tick(), 1000)
  }

  stopTicking() {
    if (this.tickTimer) clearInterval(this.tickTimer)
    this.tickTimer = null
  }

  anyActiveInDom() {
    return this.badges().some((badge) =>
      this.constructor.ACTIVE.includes(badge.dataset.state)
    )
  }

  badges() {
    return Array.from(this.element.querySelectorAll("[data-yt-status-badge]"))
  }

  panelFor(credentialId) {
    return this.element.querySelector(`[data-yt-status-panel="${credentialId}"]`)
  }

  // Advances every active card's elapsed counter between polls, so the page is
  // visibly alive rather than merely not-yet-refreshed.
  tick() {
    this.badges().forEach((badge) => {
      if (!this.constructor.ACTIVE.includes(badge.dataset.state)) return

      const panel = this.panelFor(badge.dataset.ytStatusBadge)
      const el = panel?.querySelector("[data-yt-status-elapsed]")
      const startedAt = el?.dataset.startedAt
      if (!el || !startedAt) return

      const seconds = Math.max(0, Math.round((Date.now() - Date.parse(startedAt)) / 1000))
      el.textContent = `(${this.duration(seconds)})`
    })
  }

  async poll() {
    if (++this.attempts > this.maxAttemptsValue) return this.stopPolling()

    let data
    try {
      const res = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!res.ok) return // transient — keep polling
      data = await res.json()
    } catch (_e) {
      return // transient network error — keep polling
    }

    this.render(data)

    // Reload only on the edge from active to settled, and only if we actually saw a
    // run in flight — otherwise a page opened while idle would refresh itself forever.
    if (!data.any_active) {
      this.stopPolling()
      if (this.sawActive) this.reload()
    }
  }

  render(data) {
    (data.runs || []).forEach((run) => this.renderRun(run))
  }

  renderRun(run) {
    const badge = this.element.querySelector(`[data-yt-status-badge="${run.credential_id}"]`)
    if (!badge) return

    const state = run.state || "never"
    badge.dataset.state = state

    const pill = badge.querySelector("[data-yt-status-pill]")
    const label = badge.querySelector("[data-yt-status-label]")
    if (label) label.textContent = this.constructor.LABELS[state] || state
    if (pill) this.applyClasses(pill, state)

    const dot = badge.querySelector("[data-yt-status-dot]")
    if (dot) dot.hidden = !this.constructor.ACTIVE.includes(state)

    this.renderPanel(run, state)
  }

  renderPanel(run, state) {
    const panel = this.panelFor(run.credential_id)
    if (!panel) return

    const elapsed = panel.querySelector("[data-yt-status-elapsed]")
    if (elapsed) {
      elapsed.textContent = this.constructor.ACTIVE.includes(state)
        ? `(${this.duration(run.elapsed_s)})`
        : ""
    }

    const error = panel.querySelector("[data-yt-status-error]")
    if (error) error.textContent = run.error_message || ""

    // Totals are meaningless mid-pass — a partial count reads as a final one.
    const totals = panel.querySelector("[data-yt-status-totals]")
    if (totals && this.constructor.ACTIVE.includes(state)) totals.textContent = ""
  }

  applyClasses(pill, state) {
    Object.values(this.constructor.CLASSES).forEach((classes) =>
      pill.classList.remove(...classes.split(" "))
    )
    const next = this.constructor.CLASSES[state]
    if (next) pill.classList.add(...next.split(" "))
  }

  duration(seconds) {
    const total = Number(seconds) || 0
    if (total < 60) return `${total}s`

    return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`
  }

  reload() {
    window.location.reload()
  }
}
